import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useState, useCallback, useEffect } from 'react'
import { useSessionStore } from '@/stores/sessionStore'
import { Globe, ArrowRight, Wifi, WifiOff, Leaf } from 'lucide-react'

export const Route = createFileRoute('/connect')({
    component: ConnectPage,
})

async function testConnection(url: string): Promise<{ reachable: boolean; corsOk: boolean }> {
    // `credentials: 'omit'` is load-bearing. The API uses Bearer tokens, so
    // cookies on a connectivity probe buy us nothing — but if upstream is
    // gated by HTTP Basic Auth (staging/preview subdomains often are), a
    // credentialed fetch that gets a 401 + `WWW-Authenticate: Basic` lets
    // Chrome intercept with the native sign-in dialog. Users expect to
    // authenticate via the app's form on /auth, not via the browser shell.
    try {
        await fetch(`${url}/ht/api/status`, {
            method: 'GET',
            credentials: 'omit',
            headers: {
                'X-Requested-With': 'XMLHttpRequest',
                'X-Barnet-Role': 'pos',
            },
            signal: AbortSignal.timeout(10000),
        })

        // Any HTTP response (including 401/403) proves the host is reachable
        // and CORS lets us read the status line — that's all this probe needs
        // to assert. App-level auth is the next page's problem.
        return { reachable: true, corsOk: true }
    } catch {
    }

    try {
        await fetch(`${url}/ht/api/status`, {
            method: 'GET',
            mode: 'no-cors',
            credentials: 'omit',
            signal: AbortSignal.timeout(10000),
        })

        return { reachable: true, corsOk: false }
    } catch {

        return { reachable: false, corsOk: false }
    }
}

function ConnectPage() {
    const navigate = useNavigate()
    const { backendUrl, setBackendUrl } = useSessionStore()

    const savedSubdomain = backendUrl
        ? backendUrl.replace('https://', '').replace('.waio.app', '')
        : ''

    const [subdomain, setSubdomain] = useState(savedSubdomain)
    const [isConnecting, setIsConnecting] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const [warning, setWarning] = useState<string | null>(null)
    const [isConnected, setIsConnected] = useState(false)

    useEffect(() => {
        if (backendUrl && !isConnecting) {
            setSubdomain(backendUrl.replace('https://', '').replace('.waio.app', ''))
        }
    }, [backendUrl, isConnecting])

    const isCustomUrl = subdomain.includes('localhost') || subdomain.includes('http') || /^[0-9]+\./.test(subdomain)

    const handleConnect = useCallback(async () => {
        const trimmed = subdomain.trim().toLowerCase()
        if (!trimmed) {
            setError('Please enter a subdomain or URL')
            return
        }

        const isInputCustom = trimmed.includes('localhost') || trimmed.includes('http') || /^[0-9]+\./.test(trimmed)

        if (!isInputCustom && !/^[a-z0-9]([a-z0-9-]*[a-z0-9])?$/.test(trimmed)) {
            setError('Invalid format')
            return
        }

        setIsConnecting(true)
        setError(null)
        setWarning(null)

        let url = `https://${trimmed}.waio.app`
        if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) {
            url = trimmed
        } else if (isInputCustom) {
            url = `http://${trimmed}`
        }

        url = url.replace(/\/$/, '')

        try {
            const result = await testConnection(url)

            if (!result.reachable) {
                setError('Could not reach server. Check the subdomain and your connection.')
                return
            }

            setBackendUrl(url)
            setIsConnected(true)

            if (!result.corsOk) {
                setWarning('Connected, but CORS may not be configured. API calls might require a proxy.')
            }

            setTimeout(() => {
                navigate({ to: '/auth' })
            }, 600)
        } catch {
            setError('Could not connect to server. Please try again.')
        } finally {
            setIsConnecting(false)
        }
    }, [subdomain, setBackendUrl, navigate])

    const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
        if (e.key === 'Enter' && subdomain.trim()) {
            handleConnect()
        }
    }, [handleConnect, subdomain])

    const handleContinue = useCallback(() => {
        if (backendUrl) {
            navigate({ to: '/auth' })
        }
    }, [backendUrl, navigate])

    return (
        <div className="min-h-screen flex flex-col items-center justify-center bg-gradient-to-br from-green-600 to-green-800 p-8">
            <div className="w-full max-w-md">
                {/* Logo */}
                <div className="text-center mb-8">
                    <div className="inline-flex items-center justify-center w-20 h-20 bg-white/10 backdrop-blur rounded-2xl mb-4">
                        <Leaf className="w-10 h-10 text-white" />
                    </div>
                    <h1 className="text-xl font-bold text-white">WAIO POS</h1>
                    <p className="text-green-100 mt-2">Connect to your store server</p>
                </div>

                {/* Connection Card */}
                <div className="bg-white rounded-2xl shadow-xl p-6">
                    <div className="flex items-center gap-3 mb-6">
                        <div className="w-10 h-10 bg-green-100 rounded-xl flex items-center justify-center">
                            <Globe className="w-5 h-5 text-green-600" />
                        </div>
                        <div>
                            <h2 className="font-semibold text-gray-900">Server Connection</h2>
                            <p className="text-sm text-gray-500">Enter your store's subdomain</p>
                        </div>
                    </div>

                    {/* Subdomain Input */}
                    <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Subdomain
                        </label>
                        <div className="flex items-center gap-0">
                            <input
                                type="text"
                                value={subdomain}
                                onChange={(e) => {
                                    setSubdomain(e.target.value.toLowerCase())
                                    setError(null)
                                    setWarning(null)
                                    setIsConnected(false)
                                }}
                                onKeyDown={handleKeyDown}
                                placeholder={isCustomUrl ? "http://..." : "storename"}
                                className={`flex-1 h-12 px-4 bg-gray-50 border border-gray-200 text-gray-900 text-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent ${isCustomUrl ? 'rounded-xl' : 'rounded-l-xl border-r-0'
                                    }`}
                                autoFocus
                                autoCapitalize="none"
                                autoCorrect="off"
                                spellCheck={false}
                            />
                            {!isCustomUrl && (
                                <div className="h-12 px-4 bg-gray-100 border border-l-0 border-gray-200 rounded-r-xl flex items-center text-gray-500 text-sm font-medium">
                                    .waio.app
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Error */}
                    {error && (
                        <div className="flex items-center gap-2 mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                            <WifiOff className="w-4 h-4 text-red-500 flex-shrink-0" />
                            <p className="text-sm text-red-600">{error}</p>
                        </div>
                    )}

                    {/* Warning */}
                    {warning && (
                        <div className="flex items-center gap-2 mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                            <Wifi className="w-4 h-4 text-amber-500 flex-shrink-0" />
                            <p className="text-sm text-amber-700">{warning}</p>
                        </div>
                    )}

                    {/* Success */}
                    {isConnected && !warning && (
                        <div className="flex items-center gap-2 mb-4 p-3 bg-green-50 border border-green-200 rounded-lg">
                            <Wifi className="w-4 h-4 text-green-500 flex-shrink-0" />
                            <p className="text-sm text-green-600">Connected successfully!</p>
                        </div>
                    )}

                    {/* Connect Button */}
                    <button
                        onClick={handleConnect}
                        disabled={!subdomain.trim() || isConnecting}
                        className="w-full h-12 bg-green-500 hover:bg-green-600 disabled:bg-gray-300 disabled:cursor-not-allowed text-white font-semibold rounded-xl flex items-center justify-center gap-2 transition-colors cursor-pointer"
                    >
                        {isConnecting ? (
                            <>
                                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                                Connecting...
                            </>
                        ) : (
                            <>
                                Connect
                                <ArrowRight className="w-5 h-5" />
                            </>
                        )}
                    </button>

                    {/* Continue with existing connection */}
                    {backendUrl && !isConnecting && !isConnected && (
                        <button
                            onClick={handleContinue}
                            className="w-full mt-3 h-10 text-green-600 hover:bg-green-50 font-medium rounded-xl flex items-center justify-center gap-1 transition-colors cursor-pointer text-sm"
                        >
                            Continue with {backendUrl.replace('https://', '')}
                        </button>
                    )}
                </div>

                {/* Footer */}
                <p className="text-center text-green-100/60 text-sm mt-6">
                    v2.0.0 • © 2026 WAIO
                </p>
            </div>
        </div>
    )
}
